<template lang="pug">
.localization-settings
    el-form-item.no-margin
        el-switch(
            :active-text="$t('Localization')"
            :value="i18n"
            @input="onI18nChange"
        )

    transition(name="slide-decent-vertical")
        el-form-item.mt-2(v-if="i18n" :label="$t('Languages')")
            el-select.is-full-width(
                placeholder=""
                :value="languages"
                @input="onLanguagesChange"
                valueKey="code"
                :class="{ 'loading': loading }"
                multiple
                filterable
            )
                el-option(
                    v-for="item in languageOptions"
                    :key="item.code"
                    :label="getLanguageName(item.code)"
                    :value="item"
                )
                    .flex-row
                        .col
                            span.i18n-flag.circle.fi.fis.mr-1(:class="`fi-${getLanguageFlag(item.code)}`")
                            span {{ getLanguageName(item.code) }}
                        .col.gray-text
                            span {{ getLanguageNativeName(item.code) }}

    transition(name="slide-decent-vertical")
        table.languages-table(v-if="i18n && languages.length")
            thead
                tr
                    th(align="center")
                    th(align="center") {{ $t('Active') }}
                    th(align="left") {{ $t('Language') }}
                    th(align="center") {{ $t('Flag') }}
                    th(align="center")
                        nobr {{ $t('Default Language') }}
            draggable(
                :value="languages"
                @input="onLanguagesChange"
                tag="tbody"
                group="languages"
                handle=".grip"
            )
                tr(v-for="(language, i) in languages" :key="language.code")
                    td.shrink(align="center")
                        <svg class="grip" height="1rem" fill="#ccc" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512"><!--!Font Awesome Free 6.5.2 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license/free Copyright 2024 Fonticons, Inc.--><path d="M40 352l48 0c22.1 0 40 17.9 40 40l0 48c0 22.1-17.9 40-40 40l-48 0c-22.1 0-40-17.9-40-40l0-48c0-22.1 17.9-40 40-40zm192 0l48 0c22.1 0 40 17.9 40 40l0 48c0 22.1-17.9 40-40 40l-48 0c-22.1 0-40-17.9-40-40l0-48c0-22.1 17.9-40 40-40zM40 320c-22.1 0-40-17.9-40-40l0-48c0-22.1 17.9-40 40-40l48 0c22.1 0 40 17.9 40 40l0 48c0 22.1-17.9 40-40 40l-48 0zM232 192l48 0c22.1 0 40 17.9 40 40l0 48c0 22.1-17.9 40-40 40l-48 0c-22.1 0-40-17.9-40-40l0-48c0-22.1 17.9-40 40-40zM40 160c-22.1 0-40-17.9-40-40L0 72C0 49.9 17.9 32 40 32l48 0c22.1 0 40 17.9 40 40l0 48c0 22.1-17.9 40-40 40l-48 0zM232 32l48 0c22.1 0 40 17.9 40 40l0 48c0 22.1-17.9 40-40 40l-48 0c-22.1 0-40-17.9-40-40l0-48c0-22.1 17.9-40 40-40z"/></svg>
                    td.shrink(align="center")
                        el-switch(
                            :disabled="language.default"
                            :value="language.default || language.status === 'enabled'"
                            @change="toggleLanguageStatus(language)"
                        )
                    td(align="left")
                        span {{ getLanguageName(language.code) }}
                    td(align="center")
                        a.i18n-flag.circle.fi.fis(
                            href
                            @click.prevent="openLanguageFlagDialog(language)"
                            :class="`fi-${language.flag || getLanguageFlag(language.code)}`"
                        )
                    td.shrink(align="center")
                        el-switch(:value="language.default" @change="setDefaultLanguage(language)")

    .preload-flags
        .i18n-flag.circle.fi.fis(
            v-for="country in flagDialog.countries"
            :class="`fi-${country}`"
        )

    el-dialog(
        :title="$t('Select flag for language: {language}', { language: getLanguageName(flagDialog.language) })"
        :visible.sync="flagDialog.visibility"
        width="514px"
    )
        .flags
            a.i18n-flag.circle.fi.fis(
                v-for="country in flagDialog.countries"
                href
                @click.prevent="flagDialog.flag && flagDialog.flag === country ? flagDialog.flag = null : flagDialog.flag = country"
                :class="[`fi-${country}`, { active: flagDialog.flag === country, inactive: flagDialog.flag && flagDialog.flag !== country }]"
            )
        span(slot="footer")
            .modal-footer
                .left
                    el-button(
                        size="mini"
                        icon="el-icon-fa-times fas"
                        @click="closeFlagDialog"
                    ) {{ $t('Cancel') }}
                .right
                    el-button(
                        size="mini"
                        type="primary"
                        icon="el-icon-fa-check fas"
                        @click="submitFlagDialog"
                    ) {{ $t('Ok') }}
</template>

<i18n>
{
    "en": {
        "Select flag for language: {language}": "Select flag for language: {language}"
    },
    "de": {
        "Localization": "Mehrsprachigkeit",
        "Languages": "Sprachen",
        "Language": "Sprache",
        "Active": "Aktiv",
        "Flag": "Flagge",
        "Default Language": "Standardsprache",
        "Select flag for language: {language}": "Flagge für Sprache \"{language}\" auswählen",
        "Cancel": "Abbrechen",
        "Ok": "Ok"
    }
}
</i18n>

<script>
import _ from 'lodash';
import draggable from 'vuedraggable';
import countries from 'i18n-iso-countries';
import languages from '@cospired/i18n-iso-languages';
import { language as languageFilter, languageFlag, languageNativeName } from '@/filters';
import { useSetupStore } from '@/stores';

languages.registerLocale(require('@cospired/i18n-iso-languages/langs/en.json'));
languages.registerLocale(require('@cospired/i18n-iso-languages/langs/de.json'));

export default {
    name: 'LocalizationSettings',
    components: {
        draggable
    },
    props: {
        i18n: {
            type: Boolean,
            default: false
        },
        languages: {
            type: Array,
            default: () => []
        },
        loading: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            flagDialog: {
                visibility: false,
                countries: Object.keys(countries.getAlpha2Codes())
                    .map(el => el.toLowerCase())
                    .filter(el => !['mq', 'gf', 'gp', 'yt', 're', 'bl', 'mf', 'pm', 'um', 'wf'].includes(el)),
                language: null,
                flag: null
            }
        };
    },
    computed: {
        languageOptions() {
            const localeValue = this.locale ? this.locale.value : 'de';
            return Object.entries(languages.getNames(localeValue))
                .map(([code, name]) => ({ code, name }));
        },
        setupStore() {
            return useSetupStore();
        },
        locale() {
            return this.setupStore.localeObject;
        }
    },
    methods: {
        onI18nChange(value) {
            this.$emit('update:i18n', value);
            if (value && !this.languages.length) {
                // Auto-add German as default when enabling i18n
                this.$emit('update:languages', [{ code: 'de', status: 'enabled', default: true, position: 1 }]);
            }
        },
        onLanguagesChange(value) {
            const languages = value.map((lang, i) => {
                const existing = this.languages.find(l => l.code === lang.code);
                return {
                    ...lang,
                    position: i + 1,
                    status: existing ? existing.status : 'disabled',
                    default: existing ? existing.default : false
                };
            });

            // Ensure at least one default
            if (languages.length && !languages.find(l => l.default)) {
                languages[0].default = true;
            }

            this.$emit('update:languages', languages);
        },
        toggleLanguageStatus(language) {
            const languages = _.cloneDeep(this.languages);
            languages.forEach(lang => {
                if (lang.code === language.code) {
                    lang.status = lang.status === 'enabled' ? 'disabled' : 'enabled';
                }
            });
            this.$emit('update:languages', languages);
        },
        setDefaultLanguage(language) {
            const languages = this.languages.map(lang => ({
                ...lang,
                default: lang.code === language.code
            }));
            this.$emit('update:languages', languages);
        },
        openLanguageFlagDialog(language) {
            this.flagDialog.language = language.code;
            this.flagDialog.flag = language.flag || null;
            this.flagDialog.visibility = true;
        },
        submitFlagDialog() {
            const languages = _.cloneDeep(this.languages);
            languages.forEach(lang => {
                if (lang.code === this.flagDialog.language) {
                    lang.flag = this.flagDialog.flag;
                }
            });
            this.$emit('update:languages', languages);
            this.closeFlagDialog();
        },
        closeFlagDialog() {
            this.flagDialog.visibility = false;
            this.flagDialog.language = null;
            this.flagDialog.flag = null;
        },
        getLanguageName(code) {
            return languageFilter(code, this.locale ? this.locale.value : 'de');
        },
        getLanguageFlag(code) {
            return languageFlag(code);
        },
        getLanguageNativeName(code) {
            return languageNativeName(code);
        }
    }
};
</script>

<style lang="sass" scoped>
.languages-table
    width: 100%
    background-color: #f9f9f9
    border-radius: 4px
    border: 1px solid #e9e9e9
    font-size: 14px
    td, th
        padding: 4px 10px
        &.shrink
            width: 1%
            white-space: nowrap

a.i18n-flag.circle.fi
    text-decoration: none
    width: 18px
    height: 18px
    margin-right: 0
    transition: .15s box-shadow ease, .15s transform ease
    &.inactive
        filter: grayscale(0.8)
        opacity: .6
    &.active,
    &:hover,
    &:focus
        transform: scale(1.2)
        box-shadow: 0 4px 5px 0 rgba(0,0,0,0.14), 0 1px 10px 0 rgba(0,0,0,0.12), 0 2px 4px -1px rgba(0,0,0,0.3)

.flags
    display: flex
    flex-wrap: wrap
    gap: 6px

.preload-flags
    opacity: 0
    position: absolute
    top: -9999px
    left: -9999px
    width: 1px
    height: 1px

.grip
    cursor: grab

.flex-row
    display: flex
    justify-content: space-between
    .col
        display: flex
        align-items: center
        .circle.fi
            margin-right: 0.5rem
    .gray-text
        color: #909399
</style>
